import React, { useState, useEffect, useMemo } from 'react';
import { INITIAL_PROFILE, DIFFICULTY_LEVELS, TIME_RANGES, CUISINE_TYPES } from './constants';
import { RecipeCard } from './components/recetik/RecipeCard';
import { CookingMode } from './components/recetik/CookingMode';
import { RecipeForm } from './components/recetik/RecipeForm';
import { CommentSection } from './components/recetik/CommentSection';
import { ProfileSettings } from './components/recetik/ProfileSettings';
import { Auth } from './components/recetik/Auth';
import { Collections } from './components/recetik/Collections';
import { PremiumModal, AIUsageBanner } from './components/recetik/Premium';
import { AddToCollectionButton } from './components/recetik/AddToCollection';
import { ShareRecipeButton } from './components/recetik/ShareRecipe';
import { ServingsAdjuster } from './components/recetik/ServingsAdjuster';
import { ChefProfile } from './components/recetik/ChefProfile';
import { DiscoverChefs } from './components/recetik/DiscoverChefs';
import { FollowButton } from './components/recetik/FollowButton';
import { NotificationList } from './components/recetik/NotificationList';
import { ProfileStats } from './components/recetik/ProfileStats';
import { notificationService } from './services/notificationService';
import { profileService } from './services/profileService';
import { callFunction } from './services/backendService';
import { supabase } from './lib/supabase';
import {
  Plus, Search, UtensilsCrossed, ArrowLeft, Loader2,
  Clock, Bell, ChevronRight, CookingPot,
  Sparkles, LogOut, Heart, BookOpen, X, User, MessageCircle, Settings, SlidersHorizontal, MapPin, TrendingUp, Play, Users, Globe, Crown, FolderPlus, Bookmark, Flame, UserPlus
} from 'lucide-react';
import "./App.css";

const App = () => {
  const [activeView, setActiveView] = useState('feed');
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [recipeToDelete, setRecipeToDelete] = useState(null);
  const [isCooking, setIsCooking] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [session, setSession] = useState(null);
  const [userLikes, setUserLikes] = useState([]);
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [cuisineFilter, setCuisineFilter] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [recipes, setRecipes] = useState([]);
  const [recipeComments, setRecipeComments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [isLoadingRecipes, setIsLoadingRecipes] = useState(true);
  const [aiIngredients, setAiIngredients] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [recipeCreationMode, setRecipeCreationMode] = useState('selection');
  const [toasts, setToasts] = useState([]);
  
  // Premium & AI Usage state
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [aiUsage, setAiUsage] = useState({ uses_remaining: 3, is_premium: false, total_uses: 0 });
  const [selectedCollection, setSelectedCollection] = useState(null);
  
  // Chef profile state
  const [viewingChefId, setViewingChefId] = useState(null);
  
  // Profile stats
  const [profileStats, setProfileStats] = useState(null);
  
  const [userSettings, setUserSettings] = useState({
    highContrast: false,
    dyslexicFont: false,
    largeText: false,
    isDarkMode: false,
    publicProfile: true
  });

  // Init Auth & Settings
  useEffect(() => {
    const savedSettings = localStorage.getItem('recetik_settings');
    if (savedSettings) setUserSettings(JSON.parse(savedSettings));

    supabase.auth.getSession().then(({ data: { session } }) => {
      handleSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      handleSession(session);
    });

    fetchRecipes();
    return () => subscription.unsubscribe();
  }, []);

  // Sync settings
  useEffect(() => {
    localStorage.setItem('recetik_settings', JSON.stringify(userSettings));
    const body = document.body;
    if (userSettings.isDarkMode) body.classList.add('dark');
    else body.classList.remove('dark');
    if (userSettings.dyslexicFont) body.classList.add('dyslexic');
    else body.classList.remove('dyslexic');
  }, [userSettings]);

  // Fetch contextual data & Scroll to top on recipe selection
  useEffect(() => {
    if (selectedRecipe) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      fetchComments(selectedRecipe.id);
    }
    if (isLoggedIn && session?.user) fetchNotifications();
  }, [selectedRecipe, isLoggedIn, session]);

  const handleSession = async (session) => {
    setSession(session);
    setIsLoggedIn(!!session);
    if (session?.user) {
      await syncProfile(session.user);
      await fetchUserLikes(session.user.id);
      await fetchNotifications();
      await fetchAiUsage(session.user.id);
      await fetchProfileStats();
      checkPaymentReturn();
      if (activeView === 'login') setActiveView('feed');
    } else {
      setUserProfile(null);
      setUserLikes([]);
      setNotifications([]);
      setAiUsage({ uses_remaining: 3, is_premium: false, total_uses: 0 });
      setProfileStats(null);
    }
  };

  // El usuario sale de la sesión en el servidor; userId se mantiene por compatibilidad.
  const fetchAiUsage = async (userId) => {
    try {
      const data = await callFunction('ai-usage');
      setAiUsage(data);
    } catch (e) { console.error("Error fetching AI usage:", e); }
  };

  const checkPaymentReturn = async () => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const paymentStatus = params.get('payment');
    
    if (sessionId && paymentStatus === 'success') {
      // Poll for payment status
      try {
        const data = await callFunction('payment-status', { session_id: sessionId });
        if (data.payment_status === 'paid') {
          addToast('¡Bienvenido a Recetik Premium! 🎉');
          await fetchAiUsage();
        }
      } catch (e) { console.error("Error checking payment:", e); }
      
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const fetchUserLikes = async (userId) => {
    try {
      const { data } = await supabase.from('likes').select('recipe_id').eq('user_id', userId);
      if (data) setUserLikes(data.map(l => l.recipe_id));
    } catch (e) { console.error("Error likes:", e); }
  };

  const fetchNotifications = async () => {
    if (!session?.user) return;
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select(`*, profiles:actor_id (id, display_name, username, avatar_url)`)
        .eq('recipient_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(50);
      if (data && !error) {
        setNotifications(data.map((n) => ({
          ...n,
          actor_name: n.profiles?.display_name || 'Un Chef',
          actor_username: n.profiles?.username || '',
          actor_avatar: n.profiles?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${n.actor_id}`
        })));
      }
    } catch (e) { console.error("Error notis:", e); }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await supabase.from('notifications').delete().eq('id', notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      addToast('Notificación eliminada');
    } catch (e) { 
      console.error("Error deleting notification:", e); 
    }
  };

  const markAllNotificationsRead = async () => {
    if (!session?.user) return;
    try {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('recipient_id', session.user.id)
        .eq('is_read', false);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      addToast('Todas las notificaciones marcadas como leídas');
    } catch (e) { 
      console.error("Error marking notifications read:", e); 
    }
  };

  // Fetch profile stats
  const fetchProfileStats = async () => {
    if (!session?.user) return;
    try {
      const stats = await profileService.getProfileStats(session.user.id);
      setProfileStats(stats);
    } catch (e) { console.error("Error stats:", e); }
  };

  // Increment recipes completed after cooking mode
  const handleCookingComplete = async () => {
    if (!session?.user) return;
    try {
      await profileService.incrementRecipesCompleted(session.user.id);
      // Refresh stats
      fetchProfileStats();
      addToast('¡Receta completada! +1 a tu contador 🎉');
    } catch (e) { 
      console.error("Error incrementing recipes completed:", e); 
    }
  };

  const fetchComments = async (recipeId) => {
    try {
      const { data, error } = await supabase.from('comments').select(`id, text, created_at, parent_id, author_id, profiles:author_id (display_name, avatar_url)`).eq('recipe_id', recipeId).order('created_at', { ascending: true });
      if (data && !error) {
        const formattedComments = data.map((c) => ({
          id: c.id, author: c.profiles?.display_name || 'Chef Anónimo', author_id: c.author_id, author_avatar: c.profiles?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${c.author_id}`, text: c.text, created_at: c.created_at, replies: []
        }));
        const rootComments = formattedComments.filter(c => !data.find((orig) => orig.id === c.id)?.parent_id);
        const replies = formattedComments.filter(c => data.find((orig) => orig.id === c.id)?.parent_id);
        replies.forEach(reply => {
          const parentId = data.find((orig) => orig.id === reply.id)?.parent_id;
          const parent = rootComments.find(c => c.id === parentId);
          if (parent) parent.replies = [...(parent.replies || []), reply];
        });
        setRecipeComments(rootComments);
      }
    } catch (e) { console.error("Error comments:", e); }
  };

  const handleAddComment = async (text, parentId) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !selectedRecipe) { if (!user) setActiveView('login'); return; }
    const commentText = text.trim();
    if (!commentText) return;
    try {
      const { data: newDbComment, error } = await supabase.from('comments').insert({ recipe_id: selectedRecipe.id, author_id: user.id, text: commentText, parent_id: parentId || null }).select(`id, text, created_at, parent_id, author_id, profiles:author_id (display_name, avatar_url)`).single();
      if (error) throw error;
      if (newDbComment) {
        // La notificación al autor la crea el trigger trigger_comment_notification
        const uiComment = { id: newDbComment.id, author: newDbComment.profiles?.display_name || userProfile?.display_name || 'Chef', author_id: newDbComment.author_id, author_avatar: newDbComment.profiles?.avatar_url || userProfile?.avatar_url || '', text: newDbComment.text, created_at: newDbComment.created_at, replies: [] };
        if (parentId) setRecipeComments(prev => prev.map(c => c.id === parentId ? { ...c, replies: [...(c.replies || []), uiComment] } : c));
        else setRecipeComments(prev => [...prev, uiComment]);
        addToast("¡Comentario enviado!");
      }
    } catch (e) { addToast("Error al comentar"); }
  };

  const syncProfile = async (user) => {
    try {
      const { data: existingProfile } = await supabase.from('profiles').select('*').eq('id', user.id).maybeSingle();
      if (existingProfile) { setUserProfile(existingProfile); return; }
      const profileBase = { id: user.id, username: user.email?.split('@')[0] || `chef_${user.id.slice(0, 5)}`, display_name: user.user_metadata?.display_name || 'Chef Recetik', avatar_url: user.user_metadata?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.id}`, bio: '¡Cocinando nuevas historias!', dietary_preferences: [], allergens: [], followers_count: 0, following_count: 0 };
      setUserProfile(profileBase);
      await supabase.from('profiles').upsert({ id: profileBase.id, username: profileBase.username, display_name: profileBase.display_name, avatar_url: profileBase.avatar_url, bio: profileBase.bio });
    } catch (e) { console.error("Error sync perfil:", e); }
  };

  const handleUpdateProfile = async (updatedData) => {
    if (!session?.user?.id) return;
    try {
      const { error } = await supabase.from('profiles').update(updatedData).eq('id', session.user.id);
      if (!error) { setUserProfile(prev => prev ? { ...prev, ...updatedData } : null); addToast("Perfil actualizado correctamente"); }
      else throw error;
    } catch (e) { addToast("Error al guardar cambios"); }
  };

  const fetchRecipes = async () => {
    setIsLoadingRecipes(true);
    try {
      const { data } = await supabase.from('recipes').select(`*, profiles:author_id (display_name, avatar_url)`).order('created_at', { ascending: false });
      if (data) {
        setRecipes(data.map((r) => ({
          ...r, author: r.profiles?.display_name || 'Chef Invitado', author_avatar: r.profiles?.avatar_url, servings_original: r.servings
        })));
      }
    } catch (e) { console.error("Error recetas:", e); } finally { setIsLoadingRecipes(false); }
  };

  const requireAuth = (callback) => {
    if (isLoggedIn) callback();
    else setActiveView('login');
  };

  const filteredRecipes = useMemo(() => {
    let result = [...recipes];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(r => r.title.toLowerCase().includes(q) || r.author.toLowerCase().includes(q));
    }
    if (difficultyFilter !== 'all') {
      result = result.filter(r => r.difficulty === difficultyFilter);
    }
    if (timeFilter !== 'all') {
      result = result.filter(r => {
        const mins = parseInt(r.time) || 0;
        if (timeFilter === '< 15 min') return mins < 15;
        if (timeFilter === '15-30 min') return mins >= 15 && mins <= 30;
        if (timeFilter === '30-60 min') return mins > 30 && mins <= 60;
        if (timeFilter === '> 60 min') return mins > 60;
        return true;
      });
    }
    if (cuisineFilter.length > 0) {
      result = result.filter(r => r.tags?.some(tag => cuisineFilter.includes(tag)));
    }
    return result;
  }, [recipes, searchQuery, difficultyFilter, timeFilter, cuisineFilter]);

  const trendingRecipes = useMemo(() => {
    return [...recipes].sort((a, b) => (b.likes_count || 0) - (a.likes_count || 0)).slice(0, 4);
  }, [recipes]);

  const toggleCuisineFilter = (cuisine) => {
    setCuisineFilter(prev => 
      prev.includes(cuisine) 
        ? prev.filter(c => c !== cuisine) 
        : [...prev, cuisine]
    );
  };

  const handleLike = async (recipe) => {
    requireAuth(async () => {
      const isAlreadyLiked = userLikes.includes(recipe.id);
      try {
        if (isAlreadyLiked) {
          const { error } = await supabase.from('likes').delete().eq('user_id', session.user.id).eq('recipe_id', recipe.id);
          if (error) throw error;
          const newLikesCount = Math.max(0, (recipe.likes_count || 0) - 1);
          setRecipes(prev => prev.map(r => r.id === recipe.id ? { ...r, likes_count: newLikesCount } : r));
          if (selectedRecipe?.id === recipe.id) setSelectedRecipe({ ...selectedRecipe, likes_count: newLikesCount });
          setUserLikes(prev => prev.filter(id => id !== recipe.id));
        } else {
          const { error } = await supabase.from('likes').insert({ user_id: session.user.id, recipe_id: recipe.id });
          if (error) throw error;
          // La notificación al autor la crea el trigger trigger_like_notification
          const newLikesCount = (recipe.likes_count || 0) + 1;
          setRecipes(prev => prev.map(r => r.id === recipe.id ? { ...r, likes_count: newLikesCount } : r));
          if (selectedRecipe?.id === recipe.id) setSelectedRecipe({ ...selectedRecipe, likes_count: newLikesCount });
          setUserLikes(prev => [...prev, recipe.id]);
          addToast(`¡Te encanta la receta de ${recipe.author}!`);
        }
      } catch (e) { addToast("Error de interacción"); }
    });
  };

  const handleEditRecipe = (recipe) => {
    setEditingRecipe(recipe);
    setRecipeCreationMode('manual');
    setActiveView('create');
  };

  const confirmDeleteRecipe = async () => {
    if (!recipeToDelete) return;
    setIsDeleting(true);
    try {
      await supabase.from('recipes').delete().eq('id', recipeToDelete.id);
      setRecipes(prev => prev.filter(r => r.id !== recipeToDelete.id));
      setRecipeToDelete(null);
      addToast("Receta eliminada");
    } catch (e) { addToast("Error borrar"); } finally { setIsDeleting(false); }
  };

  const addToast = (message) => {
    const id = Math.random().toString();
    setToasts(prev => [...prev, { id, notification: { id, type: 'comment', actor: 'Recetik', actor_id: 'system', actor_avatar: INITIAL_PROFILE.avatar_url, recipient_id: session?.user?.id, message, created_at: new Date().toISOString(), is_read: false } }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsLoggedIn(false);
    setActiveView('feed');
    setUserProfile(null);
    setSession(null);
  };

  const handleGenerateRecipeAI = async () => {
    if (!aiIngredients.trim()) return;
    requireAuth(async () => {
      // Check if user can use AI
      if (!aiUsage.is_premium && aiUsage.uses_remaining <= 0) {
        setShowPremiumModal(true);
        return;
      }
      
      setIsGenerating(true);
      try {
        let result;
        try {
          result = await callFunction('generate-recipe', {
            ingredients: aiIngredients.split(',').map(i => i.trim())
          });
        } catch (err) {
          if (err.code === 'AI_LIMIT_REACHED' || err.status === 403) {
            setShowPremiumModal(true);
            setAiUsage(prev => ({ ...prev, uses_remaining: 0 }));
            return;
          }
          throw err;
        }
        const payload = { 
          ...result, 
          image: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?q=80&w=800&auto=format&fit=crop', 
          tags: ['IA Chef'], 
          is_public: true, 
          author_id: session.user.id, 
          likes_count: 0 
        };
        
        const { data: newRecipe, error } = await supabase.from('recipes').insert(payload).select().single();
        if (!error && newRecipe) { 
          await fetchRecipes(); 
          // Redirect to recipe detail page
          setSelectedRecipe({
            ...newRecipe,
            author: userProfile?.display_name || 'Chef',
            author_avatar: userProfile?.avatar_url
          });
          setActiveView('feed'); 
          setRecipeCreationMode('selection'); 
          setAiIngredients('');
          // Update AI usage
          if (!aiUsage.is_premium) {
            setAiUsage(prev => ({ 
              ...prev, 
              uses_remaining: Math.max(0, prev.uses_remaining - 1),
              total_uses: prev.total_uses + 1
            }));
          }
          addToast("¡IA ha redactado tu plato!"); 
        }
      } catch (e) { 
        // Check if the error is related to AI limit
        if (e.message?.includes('limit') || e.message?.includes('límite')) {
          setShowPremiumModal(true);
          setAiUsage(prev => ({ ...prev, uses_remaining: 0 }));
        } else {
          addToast("Error Chef IA: " + e.message); 
        }
      } finally { 
        setIsGenerating(false); 
      }
    });
  };

  const saveRecipe = async (data) => {
    requireAuth(async () => {
      const isUpdating = !!editingRecipe;
      const payload = { ...data, author_id: session.user.id };
      setIsPublishing(true);
      try {
        let savedRecipe;
        if (isUpdating) {
          const { data: updated } = await supabase.from('recipes').update(payload).eq('id', editingRecipe.id).select().single();
          savedRecipe = updated;
        } else {
          const { data: created } = await supabase.from('recipes').insert({ ...payload, likes_count: 0 }).select().single();
          savedRecipe = created;
        }
        await fetchRecipes();
        // Redirect to recipe detail page
        if (savedRecipe) {
          setSelectedRecipe({
            ...savedRecipe,
            author: userProfile?.display_name || 'Chef',
            author_avatar: userProfile?.avatar_url
          });
        }
        setActiveView('feed');
        setEditingRecipe(null);
        setRecipeCreationMode('selection');
        addToast(isUpdating ? "¡Actualizada!" : "¡Publicada!");
      } catch (e) { 
        addToast("Error guardar receta"); 
      } finally {
        setIsPublishing(false);
      }
    });
  };

  const renderNav = () => (
    <header className="sticky top-0 z-40 bg-white/80 dark:bg-stone-950/80 backdrop-blur-xl border-b border-stone-100 dark:border-stone-800 px-4 md:px-6 py-4 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-3 cursor-pointer group" onClick={() => { setActiveView('feed'); setSelectedRecipe(null); setViewingChefId(null); }}>
          <div className="bg-primary p-2 rounded-2xl text-white group-hover:rotate-12 transition-transform shadow-lg shadow-primary/20">
            <UtensilsCrossed size={18} />
          </div>
          <h1 className="text-xl md:text-2xl font-black heading-serif text-stone-900 dark:text-white select-none">Recetik</h1>
        </div>
        <nav className="hidden md:flex items-center ml-4 gap-1">
          <button data-testid="nav-catalog-btn" onClick={() => { setActiveView('recipes-catalog'); setSelectedRecipe(null); setViewingChefId(null); }} className={`flex items-center gap-2 px-4 py-2 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${activeView === 'recipes-catalog' ? 'bg-primary/10 text-primary' : 'text-stone-600 dark:text-stone-300 hover:bg-stone-100/50'}`}>
            <BookOpen size={16} /> Recetario
          </button>
          <button data-testid="nav-discover-btn" onClick={() => { setActiveView('discover'); setSelectedRecipe(null); setViewingChefId(null); }} className={`flex items-center gap-2 px-4 py-2 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${activeView === 'discover' ? 'bg-primary/10 text-primary' : 'text-stone-600 dark:text-stone-300 hover:bg-stone-100/50'}`}>
            <Users size={16} /> Chefs
          </button>
        </nav>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => { setActiveView('recipes-catalog'); setSelectedRecipe(null); setViewingChefId(null); }} className={`md:hidden p-2.5 rounded-2xl ${activeView === 'recipes-catalog' ? 'bg-primary/10 text-primary' : 'text-stone-600 dark:text-stone-300'}`}>
          <BookOpen size={20} />
        </button>
        <button onClick={() => { setActiveView('discover'); setSelectedRecipe(null); setViewingChefId(null); }} className={`md:hidden p-2.5 rounded-2xl ${activeView === 'discover' ? 'bg-primary/10 text-primary' : 'text-stone-600 dark:text-stone-300'}`}>
          <Users size={20} />
        </button>
        <button data-testid="nav-notifications-btn" onClick={() => requireAuth(() => { setActiveView('notifications'); setSelectedRecipe(null); setViewingChefId(null); })} className={`p-2.5 rounded-2xl relative ${activeView === 'notifications' ? 'bg-primary/10 text-primary' : 'text-stone-600 dark:text-stone-300'}`}>
          <Bell size={20} />
          {notifications.some(n => !n.is_read) && <span className="absolute top-2.5 right-2.5 w-2 h-2 bg-primary rounded-full border-2 border-white dark:border-stone-950" />}
        </button>
        {isLoggedIn ? (
          <div data-testid="nav-profile-btn" onClick={() => { setActiveView('profile'); setSelectedRecipe(null); setViewingChefId(null); }} className={`w-9 h-9 md:w-10 md:h-10 rounded-2xl border-2 overflow-hidden cursor-pointer hover:scale-105 transition-all ${activeView === 'profile' ? 'border-primary' : 'border-primary/20'}`}>
            <img src={userProfile?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${session?.user?.id}`} className="w-full h-full object-cover" alt="Profile" />
          </div>
        ) : (
          <button data-testid="nav-login-btn" onClick={() => setActiveView('login')} className="p-2.5 md:px-6 md:py-2 text-stone-600 dark:text-stone-300 md:bg-stone-900 md:dark:bg-stone-100 md:text-white md:dark:text-stone-900 rounded-2xl font-black text-[10px] uppercase tracking-widest">
            <User size={20} className="md:hidden" /> <span className="hidden md:inline">Entrar</span>
          </button>
        )}
      </div>
    </header>
  );

  return (
    <div className={`min-h-screen transition-all ${userSettings.isDarkMode ? 'bg-stone-950 text-white' : 'bg-[#FFFBF7] text-stone-900'} ${userSettings.highContrast ? 'contrast-125 saturate-125' : ''} ${userSettings.largeText ? 'text-lg' : ''}`}>
      {!isCooking && activeView !== 'login' && renderNav()}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {activeView === 'login' ? ( <Auth onAuthSuccess={handleSession} isDarkMode={userSettings.isDarkMode} /> ) : 
        activeView === 'edit-profile' && userProfile ? (
          <ProfileSettings profile={userProfile} settings={userSettings} onSaveProfile={handleUpdateProfile} onUpdateSettings={(newSettings) => setUserSettings({...userSettings, ...newSettings})} onBack={() => setActiveView('profile')} />
        ) : selectedRecipe ? (
          <div className="max-w-5xl mx-auto animate-in fade-in duration-500 pb-20">
            <button data-testid="back-from-recipe" onClick={() => setSelectedRecipe(null)} className="mb-8 flex items-center gap-2 text-[10px] font-black uppercase text-stone-400 hover:text-primary transition-colors"><ArrowLeft size={16}/> Volver</button>
            
            {/* Chef Info Banner - Top */}
            <div className="bg-white dark:bg-stone-900 rounded-[32px] border border-stone-100 dark:border-stone-800 p-5 mb-8 flex items-center justify-between">
              <div 
                data-testid="recipe-chef-banner"
                className="flex items-center gap-4 cursor-pointer group"
                onClick={() => {
                  const chefId = selectedRecipe.author_id;
                  setSelectedRecipe(null);
                  setViewingChefId(chefId);
                  setActiveView('chef-profile');
                }}
              >
                <img 
                  src={selectedRecipe.author_avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${selectedRecipe.author_id}`} 
                  className="w-14 h-14 rounded-2xl object-cover border-2 border-stone-100 dark:border-stone-800 group-hover:border-primary transition-colors shadow-lg"
                  alt={selectedRecipe.author}
                />
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-stone-400">Receta de</p>
                  <p className="font-black text-lg text-stone-900 dark:text-white group-hover:text-primary transition-colors">@{selectedRecipe.author}</p>
                </div>
              </div>
              
              {session?.user?.id !== selectedRecipe.author_id && (
                <FollowButton 
                  chefId={selectedRecipe.author_id}
                  currentUserId={session?.user?.id}
                  size="default"
                />
              )}
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
               <div className="lg:col-span-7">
                  <div className="relative aspect-[4/3] rounded-[56px] overflow-hidden shadow-2xl mb-8 group">
                    <img src={selectedRecipe.image} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" alt={selectedRecipe.title} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    
                    {/* Recipe Info Overlay - Only time/difficulty/servings */}
                    <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                       <div className="flex gap-3 flex-wrap">
                          <span className="px-4 py-2 bg-white/90 dark:bg-stone-800/90 backdrop-blur-md rounded-2xl text-[10px] font-black uppercase tracking-widest text-stone-700 dark:text-white flex items-center gap-2">
                            <Clock size={14} className="text-primary" /> {selectedRecipe.time}
                          </span>
                          <span className={`px-4 py-2 backdrop-blur-md rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 ${
                            selectedRecipe.difficulty === 'Fácil' 
                              ? 'bg-emerald-500/90 text-white' 
                              : selectedRecipe.difficulty === 'Media'
                                ? 'bg-amber-500/90 text-white'
                                : 'bg-rose-500/90 text-white'
                          }`}>
                            <Flame size={14} /> {selectedRecipe.difficulty}
                          </span>
                          <span className="px-4 py-2 bg-white/90 dark:bg-stone-800/90 backdrop-blur-md rounded-2xl text-[10px] font-black uppercase tracking-widest text-stone-700 dark:text-white flex items-center gap-2">
                            <Users size={14} className="text-primary" /> {selectedRecipe.servings}
                          </span>
                       </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons - Like moved here */}
                  <div className="flex flex-wrap gap-3 mb-8">
                     <button 
                       data-testid="recipe-detail-like" 
                       onClick={() => handleLike(selectedRecipe)} 
                       className={`px-5 py-3 rounded-2xl font-black uppercase tracking-widest text-[10px] flex items-center gap-2 transition-all shadow-lg ${
                         userLikes.includes(selectedRecipe.id) 
                           ? 'bg-rose-500 text-white shadow-rose-500/30' 
                           : 'bg-white dark:bg-stone-800 text-stone-600 dark:text-stone-300 border border-stone-100 dark:border-stone-700 hover:bg-rose-50 hover:text-rose-500 hover:border-rose-200'
                       }`}
                     >
                       <Heart size={18} fill={userLikes.includes(selectedRecipe.id) ? "currentColor" : "none"} />
                       {selectedRecipe.likes_count || 0} Me gusta
                     </button>
                     <ShareRecipeButton recipe={selectedRecipe} />
                     {isLoggedIn && (
                       <AddToCollectionButton 
                         recipeId={selectedRecipe.id}
                         userId={session?.user?.id}
                         onSuccess={(action) => {
                           if (action === 'added') addToast('¡Receta guardada en tu colección!');
                           else if (action === 'removed') addToast('Receta eliminada de la colección');
                           else if (action === 'created') addToast('¡Colección creada y receta guardada!');
                         }}
                       />
                     )}
                  </div>
                  
                  <h2 className="text-5xl sm:text-6xl font-black heading-serif mb-6 dark:text-white leading-tight">{selectedRecipe.title}</h2>
                  
                  <p className="text-xl text-stone-500 dark:text-stone-400 italic mb-10 leading-relaxed">"{selectedRecipe.description}"</p>
                  
                  <button data-testid="start-cooking-btn" onClick={() => requireAuth(() => setIsCooking(true))} className="w-full py-8 bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 rounded-[40px] font-black uppercase tracking-[0.2em] shadow-2xl flex items-center justify-center gap-4 hover:bg-primary dark:hover:bg-primary dark:hover:text-white transition-all"><CookingPot size={28}/> Empezar Modo Cocina</button>
               </div>
               
               <div className="lg:col-span-5 space-y-8">
                  {/* Servings Adjuster - Premium Feature */}
                  <ServingsAdjuster 
                    originalServings={selectedRecipe.servings}
                    ingredients={selectedRecipe.ingredients}
                    isPremium={aiUsage.is_premium}
                    onUpgrade={() => setShowPremiumModal(true)}
                  />
                  
                  {/* Regular Ingredients (shown when premium and adjusted, otherwise hidden if ServingsAdjuster shows them) */}
                  {!aiUsage.is_premium && (
                    <div className="bg-white dark:bg-stone-900 p-8 rounded-[32px] border border-stone-100 dark:border-stone-800 shadow-sm">
                      <h3 className="text-xl font-black heading-serif dark:text-white border-b border-stone-100 dark:border-stone-800 pb-4 mb-6">Ingredientes</h3>
                      <ul className="space-y-4">
                        {selectedRecipe.ingredients?.map((ing, i) => (
                          <li key={i} className="flex justify-between items-center text-base font-medium">
                            <span className="text-stone-600 dark:text-stone-300">{ing.item}</span>
                            <span className="text-primary font-black bg-primary/5 px-3 py-1 rounded-xl text-sm">{ing.amount} {ing.unit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Tags */}
                  {selectedRecipe.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedRecipe.tags.map((tag, i) => (
                        <span key={i} className="px-4 py-2 bg-stone-100 dark:bg-stone-800 rounded-xl text-[10px] font-black uppercase tracking-widest text-stone-500">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
               </div>
               
               <div className="lg:col-span-12 mt-16 pt-16 border-t border-stone-100 dark:border-stone-800">
                  <CommentSection comments={recipeComments} onAddComment={handleAddComment} />
               </div>
            </div>
          </div>
        ) : activeView === 'feed' ? (
          <div data-testid="feed-view" className="animate-in fade-in duration-700">
             <section className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24 mb-32 pt-8">
                <div className="flex-1 space-y-10 text-center lg:text-left animate-in slide-in-from-left-8 duration-1000">
                   <div className="inline-flex items-center gap-3 px-6 py-2 bg-primary/10 text-primary rounded-full text-[10px] font-black uppercase tracking-widest">
                     <Globe size={14} /> De chefs para chefs
                   </div>
                   <h2 className="text-6xl sm:text-8xl font-black heading-serif leading-[0.95] text-stone-900 dark:text-white">
                     La cocina sabe <br/>mejor cuando <br/><span className="text-primary">se comparte.</span>
                   </h2>
                   <p className="text-xl text-stone-600 dark:text-stone-400 font-medium max-w-xl leading-relaxed">
                     Descubre, cocina y comparte miles de recetas creadas por una comunidad apasionada. De tu cocina al mundo, en un solo clic.
                   </p>
                   <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                      <button data-testid="explore-btn" onClick={() => { setActiveView('recipes-catalog'); }} className="px-12 py-6 bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 rounded-[32px] font-black uppercase tracking-widest text-xs shadow-2xl hover:bg-primary hover:text-white dark:hover:bg-primary dark:hover:text-white transition-all flex items-center gap-3">
                         Explorar Recetario <ChevronRight size={18} />
                      </button>
                      <button data-testid="create-ai-btn" onClick={() => requireAuth(() => { setEditingRecipe(null); setActiveView('create'); setRecipeCreationMode('ai'); })} className="px-12 py-6 bg-white dark:bg-stone-900 border border-stone-100 dark:border-stone-800 text-stone-900 dark:text-white rounded-[32px] font-black uppercase tracking-widest text-xs flex items-center gap-3 hover:border-primary transition-all">
                         <Sparkles size={18} className="text-primary"/> Crear con IA
                      </button>
                   </div>
                   <div className="flex items-center justify-center lg:justify-start gap-8 pt-4 opacity-50">
                      <div className="text-center lg:text-left">
                         <p className="text-2xl font-black dark:text-white">12k+</p>
                         <p className="text-[9px] font-black uppercase tracking-widest text-stone-400">Recetas</p>
                      </div>
                      <div className="text-center lg:text-left">
                         <p className="text-2xl font-black dark:text-white">5k+</p>
                         <p className="text-[9px] font-black uppercase tracking-widest text-stone-400">Chefs</p>
                      </div>
                      <div className="text-center lg:text-left">
                         <p className="text-2xl font-black dark:text-white">45k</p>
                         <p className="text-[9px] font-black uppercase tracking-widest text-stone-400">Likes</p>
                      </div>
                   </div>
                </div>

                <div className="flex-1 w-full max-w-2xl relative animate-in zoom-in-95 slide-in-from-right-8 duration-1000">
                   <div className="relative aspect-square sm:aspect-[4/5] lg:aspect-square">
                      <img 
                        src={recipes[0]?.image || 'https://images.unsplash.com/photo-1547592166-23ac45744acd?q=80&w=1200&auto=format&fit=crop'} 
                        className="w-full h-full object-cover rounded-[64px] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.2)] dark:shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] rotate-3"
                        alt="Featured Dish"
                      />
                      <div className="absolute -bottom-8 -left-8 sm:bottom-12 sm:-left-12 bg-white dark:bg-stone-900 p-6 sm:p-8 rounded-[40px] shadow-2xl border border-stone-50 dark:border-stone-800 animate-bounce-slow max-w-[280px]">
                         <div className="flex items-center gap-4 mb-4">
                            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                               <Users size={24} />
                            </div>
                            <div>
                            <p className="text-[10px] font-black uppercase tracking-widest text-stone-500 dark:text-stone-400">Chef de la semana</p>
                               <p className="font-black dark:text-white">@Elena_Gourmet</p>
                            </div>
                         </div>
                         <p className="text-xs text-stone-600 dark:text-stone-400 font-medium leading-relaxed italic">"Cualquiera puede cocinar, pero solo los valientes son libres en la cocina."</p>
                      </div>
                      <button onClick={() => recipes[0] && setSelectedRecipe(recipes[0])} className="absolute top-12 -right-6 sm:top-24 sm:-right-12 w-24 h-24 sm:w-32 sm:h-32 bg-primary rounded-full flex items-center justify-center text-white shadow-2xl shadow-primary/40 hover:scale-110 transition-transform group">
                         <Play size={32} className="group-hover:translate-x-1 transition-transform" />
                         <div className="absolute inset-0 rounded-full border-4 border-white/30 animate-ping" />
                      </button>
                   </div>
                </div>
             </section>
             
             <div className="flex items-center justify-between mb-12">
                <div className="flex items-center gap-4">
                   <div className="p-3 bg-primary/10 text-primary rounded-2xl">
                      <TrendingUp size={28} />
                   </div>
                   <h3 className="text-4xl font-black heading-serif dark:text-white">Tendencias Globales</h3>
                </div>
                <button onClick={() => setActiveView('recipes-catalog')} className="text-[10px] font-black uppercase tracking-widest text-stone-500 dark:text-stone-400 hover:text-primary transition-colors flex items-center gap-2">Ver todo el catálogo <ChevronRight size={14}/></button>
             </div>

             {isLoadingRecipes ? (
               <div className="flex justify-center py-20">
                  <Loader2 className="animate-spin text-stone-300" size={48} />
               </div>
             ) : (
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-10">
                  {trendingRecipes.length > 0 ? trendingRecipes.map(r => (
                    <div key={r.id} className="relative">
                       <RecipeCard recipe={r} onOpen={setSelectedRecipe} onLike={handleLike} isLiked={userLikes.includes(r.id)} settings={userSettings} />
                    </div>
                  )) : (
                    <div className="col-span-full py-20 text-center opacity-30">
                       <p className="font-black uppercase tracking-widest">No hay tendencias aún...</p>
                    </div>
                  )}
               </div>
             )}
          </div>
        ) : activeView === 'recipes-catalog' ? (
          <div data-testid="catalog-view" className="animate-in fade-in duration-500">
             <div className="mb-8 md:mb-16">
                <h2 className="text-4xl md:text-6xl font-black heading-serif dark:text-white mb-8 md:mb-12">Recetario</h2>
                
                <div className="space-y-6">
                  <div className="flex flex-col lg:flex-row gap-4 md:gap-6 bg-white dark:bg-stone-900 p-4 md:p-6 rounded-[32px] md:rounded-[40px] border border-stone-100 dark:border-stone-800 shadow-sm">
                    <div className="flex-1 relative">
                        <Search className="absolute left-5 md:left-6 top-1/2 -translate-y-1/2 text-stone-300" size={18} />
                        <input 
                          data-testid="search-input"
                          type="text" 
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Busca por nombre o chef..."
                          className="w-full pl-14 md:pl-16 pr-6 py-4 md:py-5 rounded-2xl md:rounded-3xl bg-stone-50 dark:bg-stone-950 border-none focus:ring-4 focus:ring-primary/5 outline-none font-bold dark:text-white placeholder:text-stone-300 transition-all text-sm md:text-base"
                        />
                    </div>
                    
                    <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4">
                        <div className="overflow-x-auto no-scrollbar -mx-4 px-4 md:mx-0 md:px-0">
                          <div className="flex bg-stone-50 dark:bg-stone-950 p-1.5 rounded-[22px] md:rounded-[24px] border border-stone-100 dark:border-stone-800 w-max md:w-auto">
                            <button 
                              onClick={() => setDifficultyFilter('all')}
                              className={`px-5 md:px-6 py-2.5 md:py-3 rounded-[16px] md:rounded-[18px] text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${difficultyFilter === 'all' ? 'bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 shadow-md' : 'text-stone-400 hover:text-stone-600'}`}
                            >
                              Todas
                            </button>
                            {DIFFICULTY_LEVELS.map(lvl => (
                              <button 
                                key={lvl}
                                onClick={() => setDifficultyFilter(lvl)}
                                className={`px-5 md:px-6 py-2.5 md:py-3 rounded-[16px] md:rounded-[18px] text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${difficultyFilter === lvl ? 'bg-primary text-white shadow-md shadow-primary/20' : 'text-stone-400 hover:text-stone-600'}`}
                              >
                                {lvl}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="relative flex-1 md:flex-none">
                            <select 
                              value={timeFilter}
                              onChange={(e) => setTimeFilter(e.target.value)}
                              className="w-full pl-10 md:pl-12 pr-8 md:pr-10 py-3.5 md:py-4 rounded-[20px] md:rounded-[24px] bg-stone-50 dark:bg-stone-950 border border-stone-100 dark:border-stone-800 text-[9px] md:text-[10px] font-black uppercase tracking-widest text-stone-600 dark:text-stone-300 outline-none focus:ring-4 focus:ring-primary/5 appearance-none cursor-pointer"
                            >
                              <option value="all">Tiempo</option>
                              {TIME_RANGES.map(range => <option key={range} value={range}>{range}</option>)}
                            </select>
                            <Clock className="absolute left-4 md:left-5 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                            <SlidersHorizontal className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" size={12} />
                          </div>

                          {(searchQuery || difficultyFilter !== 'all' || timeFilter !== 'all' || cuisineFilter.length > 0) && (
                            <button 
                              data-testid="clear-filters-btn"
                              onClick={() => { setSearchQuery(''); setDifficultyFilter('all'); setTimeFilter('all'); setCuisineFilter([]); }}
                              className="p-3.5 md:p-4 bg-rose-50 dark:bg-rose-900/10 text-rose-500 rounded-xl md:rounded-2xl hover:bg-rose-500 hover:text-white transition-all group border border-rose-100 dark:border-rose-900/30"
                              title="Limpiar filtros"
                            >
                              <X size={18} className="group-hover:rotate-90 transition-transform" />
                            </button>
                          )}
                        </div>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-stone-900 p-4 md:p-6 rounded-[32px] md:rounded-[40px] border border-stone-100 dark:border-stone-800 shadow-sm animate-in fade-in slide-in-from-top-4 duration-500">
                    <div className="flex items-center gap-3 mb-4 ml-2">
                       <MapPin size={14} className="text-primary" />
                       <h4 className="text-[10px] font-black uppercase tracking-widest text-stone-400">Tipo de Cocina</h4>
                    </div>
                    <div className="overflow-x-auto no-scrollbar pb-1">
                      <div className="flex flex-wrap md:flex-nowrap gap-2 md:gap-3">
                        {CUISINE_TYPES.map(cuisine => (
                          <button 
                            key={cuisine}
                            onClick={() => toggleCuisineFilter(cuisine)}
                            className={`px-4 md:px-5 py-2.5 md:py-3 rounded-xl md:rounded-2xl text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap border ${cuisineFilter.includes(cuisine) ? 'bg-primary border-primary text-white shadow-lg shadow-primary/20' : 'bg-stone-50 dark:bg-stone-950 border-stone-100 dark:border-stone-800 text-stone-400 hover:text-stone-600 hover:border-stone-200'}`}
                          >
                            {cuisine}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex items-center px-2">
                   <p className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-stone-500 dark:text-stone-400">
                     Viendo <span className="text-stone-900 dark:text-stone-100 font-bold">{filteredRecipes.length}</span> {filteredRecipes.length === 1 ? 'receta' : 'recetas'}
                   </p>
                </div>
             </div>

             {filteredRecipes.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-10">
                   {filteredRecipes.map(r => (
                     <div key={r.id} className="animate-in fade-in zoom-in-95 duration-500">
                        <RecipeCard recipe={r} onOpen={setSelectedRecipe} onLike={handleLike} isLiked={userLikes.includes(r.id)} settings={userSettings} />
                     </div>
                   ))}
                </div>
             ) : (
                <div className="text-center py-20 md:py-40 bg-white dark:bg-stone-900 rounded-[40px] md:rounded-[64px] border border-dashed border-stone-100 dark:border-stone-800 animate-in fade-in zoom-in-95 duration-700 mx-1">
                   <div className="w-16 h-16 md:w-24 md:h-24 bg-stone-50 dark:bg-stone-800 rounded-full flex items-center justify-center mx-auto mb-6 md:mb-8 text-stone-200">
                      <Search size={48} className="hidden md:block" />
                   </div>
                   <h3 className="text-2xl md:text-3xl font-black heading-serif dark:text-white mb-3 md:mb-4">Sin coincidencias</h3>
                   <p className="text-stone-500 dark:text-stone-400 text-sm font-medium max-w-[240px] md:max-w-sm mx-auto mb-8 md:mb-10 leading-relaxed">Prueba a simplificar tu búsqueda o quita algunos filtros.</p>
                   <button 
                    onClick={() => { setSearchQuery(''); setDifficultyFilter('all'); setTimeFilter('all'); setCuisineFilter([]); }}
                    className="px-6 md:px-8 py-3.5 md:py-4 bg-primary text-white rounded-xl md:rounded-2xl font-black uppercase tracking-widest text-[9px] md:text-[10px] shadow-xl shadow-primary/20 hover:scale-105 transition-transform"
                   >
                     Limpiar todos los filtros
                   </button>
                </div>
             )}
          </div>
        ) : activeView === 'notifications' ? (
          <div data-testid="notifications-view" className="max-w-2xl mx-auto animate-in fade-in duration-500">
            <h2 className="text-4xl font-black heading-serif mb-8 dark:text-white text-center">Actividad Reciente</h2>
            <NotificationList 
              notifications={notifications}
              onDelete={deleteNotification}
              onClick={(n) => {
                // Si es notificación de follow, ir al perfil
                if (n.type === 'follow' && n.actor_id) {
                  setViewingChefId(n.actor_id);
                  setActiveView('chef-profile');
                } else {
                  // Si es like/comment, ir a la receta
                  const target = recipes.find(r => r.id === n.target_id);
                  if (target) setSelectedRecipe(target);
                }
                // Marcar como leída
                if (!n.is_read) {
                  supabase.from('notifications').update({ is_read: true }).eq('id', n.id);
                  setNotifications(prev => prev.map(notif => 
                    notif.id === n.id ? { ...notif, is_read: true } : notif
                  ));
                }
              }}
              onMarkAllRead={markAllNotificationsRead}
              hasUnread={notifications.some(n => !n.is_read)}
            />
          </div>
        ) : activeView === 'discover' ? (
          <DiscoverChefs 
            currentUserId={session?.user?.id}
            onSelectChef={(chefId) => {
              setViewingChefId(chefId);
              setActiveView('chef-profile');
            }}
          />
        ) : activeView === 'chef-profile' && viewingChefId ? (
          <ChefProfile 
            chefId={viewingChefId}
            currentUserId={session?.user?.id}
            onBack={() => {
              setViewingChefId(null);
              setActiveView('feed');
            }}
            onOpenRecipe={(recipe) => setSelectedRecipe(recipe)}
            userLikes={userLikes}
            onLike={handleLike}
            settings={userSettings}
          />
        ) : activeView === 'profile' ? (
          <div data-testid="profile-view" className="animate-in fade-in duration-500">
             <div className="bg-white dark:bg-stone-900 rounded-[64px] border border-stone-100 dark:border-stone-800 p-12 mb-12 flex flex-col items-center text-center shadow-sm relative overflow-hidden group">
                <div className="absolute top-8 right-8 z-10 flex gap-2">
                  {aiUsage.is_premium && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary to-amber-500 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-lg">
                      <Crown size={14} /> Premium
                    </div>
                  )}
                  <button data-testid="edit-profile-btn" onClick={() => setActiveView('edit-profile')} className="p-4 bg-stone-50 dark:bg-stone-800 rounded-2xl text-stone-400 hover:text-primary hover:bg-primary/10 transition-all shadow-sm"><Settings size={20} /></button>
                </div>
                <img src={userProfile?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${session?.user?.id}`} className="w-40 h-40 rounded-[48px] border-8 border-white dark:border-stone-800 shadow-2xl mb-6 object-cover" alt="Avatar" />
                <h2 className="text-5xl font-black heading-serif mb-2 dark:text-white">{userProfile?.display_name}</h2>
                <p className="text-stone-500 dark:text-stone-400 font-bold mb-2">@{userProfile?.username}</p>
                {userProfile?.bio && <p className="text-stone-500 dark:text-stone-400 mb-6 italic max-w-md">"{userProfile?.bio}"</p>}
                <div className="flex flex-wrap justify-center gap-3 mb-8">
                  {userProfile?.dietary_preferences?.map(pref => <span key={pref} className="px-4 py-1.5 bg-primary/5 text-primary rounded-xl text-[10px] font-black uppercase tracking-widest">{pref}</span>)}
                  {userProfile?.allergens?.map(alg => <span key={alg} className="px-4 py-1.5 bg-rose-50 text-rose-500 rounded-xl text-[10px] font-black uppercase tracking-widest">{alg}</span>)}
                </div>
                
                {!aiUsage.is_premium && (
                  <button 
                    onClick={() => setShowPremiumModal(true)}
                    className="mb-6 px-8 py-4 bg-gradient-to-r from-primary to-amber-500 text-white rounded-2xl font-black uppercase text-[10px] shadow-xl hover:scale-105 transition-transform flex items-center gap-2"
                  >
                    <Crown size={16}/> Hazte Premium
                  </button>
                )}
                
                <button data-testid="logout-btn" onClick={handleLogout} className="px-8 py-4 bg-rose-50 dark:bg-rose-500/10 text-rose-500 rounded-2xl font-black uppercase text-[10px] hover:bg-rose-500 hover:text-white transition-all flex items-center gap-2"><LogOut size={16}/> Cerrar Sesión</button>
             </div>
             
             {/* Profile Stats */}
             <ProfileStats 
               userId={session?.user?.id}
               currentUserId={session?.user?.id}
               initialStats={profileStats}
               onViewChef={(chefId) => {
                 setViewingChefId(chefId);
                 setActiveView('chef-profile');
               }}
             />
             
             {/* Collections Section */}
             <div className="mb-16">
               <Collections 
                 userId={session?.user?.id}
                 recipes={recipes}
                 onSelectCollection={(collection) => setSelectedCollection(collection)}
                 onOpenRecipe={(recipe) => setSelectedRecipe(recipe)}
               />
             </div>
             
             <h3 className="text-3xl font-black heading-serif mb-10 dark:text-white">Mis Creaciones</h3>
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-10">
                {recipes.filter(r => r.author_id === session?.user?.id).map(r => (
                  <RecipeCard key={r.id} recipe={r} onOpen={setSelectedRecipe} onLike={handleLike} isLiked={userLikes.includes(r.id)} settings={userSettings} isAuthor onEdit={handleEditRecipe} onDelete={() => setRecipeToDelete(r)} />
                ))}
             </div>
          </div>
        ) : activeView === 'create' ? (
          <div data-testid="create-view" className="max-w-4xl mx-auto">
             {/* Publishing Animation Overlay */}
             {(isPublishing || isGenerating) && (
               <div className="fixed inset-0 z-[100] flex items-center justify-center bg-stone-900/70 backdrop-blur-md animate-in fade-in">
                 <div className="bg-white dark:bg-stone-900 p-12 rounded-[48px] shadow-2xl text-center animate-in zoom-in-95">
                   <div className="w-24 h-24 mx-auto mb-6 bg-primary/10 rounded-full flex items-center justify-center animate-pulse">
                     <CookingPot size={48} className="text-primary animate-bounce" />
                   </div>
                   <h3 className="text-2xl font-black heading-serif text-stone-900 dark:text-white mb-2">
                     {isGenerating ? 'Chef IA cocinando...' : 'Publicando receta...'}
                   </h3>
                   <p className="text-stone-500 dark:text-stone-400 font-medium">
                     {isGenerating ? 'Creando tu receta personalizada' : 'Tu receta estará lista en segundos'}
                   </p>
                   <div className="mt-6 flex justify-center gap-2">
                     <div className="w-3 h-3 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                     <div className="w-3 h-3 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                     <div className="w-3 h-3 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                   </div>
                 </div>
               </div>
             )}
             {recipeCreationMode === 'selection' ? (
               <div className="text-center pt-20">
                  <h2 className="text-5xl font-black heading-serif mb-12 dark:text-white">Nueva Receta</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                     <div data-testid="ai-create-option" onClick={() => setRecipeCreationMode('ai')} className="p-16 bg-white dark:bg-stone-900 rounded-[56px] cursor-pointer hover:border-primary border border-stone-100 dark:border-stone-800 transition-all shadow-sm group relative overflow-hidden">
                       {!aiUsage.is_premium && aiUsage.uses_remaining <= 0 && (
                         <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center z-10">
                           <div className="text-center">
                             <Crown size={32} className="mx-auto mb-2 text-primary" />
                             <p className="text-white font-black text-sm">Premium requerido</p>
                           </div>
                         </div>
                       )}
                       <Sparkles size={40} className="mx-auto mb-8 text-primary group-hover:scale-110 transition-transform"/>
                       <h3 className="text-3xl font-black heading-serif dark:text-white">Chef IA</h3>
                       {!aiUsage.is_premium && (
                         <p className="text-sm text-stone-400 mt-4">{aiUsage.uses_remaining} usos gratuitos</p>
                       )}
                       {aiUsage.is_premium && (
                         <p className="text-sm text-primary mt-4 flex items-center justify-center gap-1"><Crown size={14}/> Ilimitado</p>
                       )}
                     </div>
                     <div data-testid="manual-create-option" onClick={() => setRecipeCreationMode('manual')} className="p-16 bg-white dark:bg-stone-900 rounded-[56px] cursor-pointer hover:border-stone-900 dark:hover:border-stone-600 border border-stone-100 dark:border-stone-800 transition-all shadow-sm group">
                       <Plus size={40} className="mx-auto mb-8 text-stone-400 group-hover:scale-110 transition-transform"/>
                       <h3 className="text-3xl font-black heading-serif dark:text-white">Manual</h3>
                       <p className="text-sm text-stone-400 mt-4">Con foto personalizada</p>
                     </div>
                  </div>
               </div>
             ) : recipeCreationMode === 'ai' ? (
               <div className="max-w-2xl mx-auto pt-10">
                  <div className="mb-8">
                    <AIUsageBanner 
                      usesRemaining={aiUsage.uses_remaining}
                      isPremium={aiUsage.is_premium}
                      onUpgrade={() => setShowPremiumModal(true)}
                    />
                  </div>
                  
                  <h2 className="text-5xl font-black heading-serif mb-6 dark:text-white">¿Qué tienes hoy?</h2>
                  <p className="text-stone-500 dark:text-stone-400 mb-8">Escribe los ingredientes que tienes y el Chef IA creará una receta perfecta para ti.</p>
                  
                  <textarea 
                    data-testid="ai-ingredients-input" 
                    value={aiIngredients} 
                    onChange={e => setAiIngredients(e.target.value)} 
                    placeholder="Tomate, Queso, Albahaca, Pasta..." 
                    className="w-full h-44 p-8 bg-white dark:bg-stone-900 border border-stone-100 dark:border-stone-800 rounded-[40px] outline-none focus:border-primary transition-all text-xl mb-6 resize-none dark:text-white shadow-sm placeholder:text-stone-300 dark:placeholder:text-stone-600" 
                  />
                  
                  <button 
                    data-testid="generate-ai-recipe-btn" 
                    onClick={handleGenerateRecipeAI} 
                    disabled={isGenerating || !aiIngredients.trim() || (!aiUsage.is_premium && aiUsage.uses_remaining <= 0)} 
                    className="w-full py-8 bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 rounded-[32px] font-black uppercase flex items-center justify-center gap-4 disabled:opacity-50 hover:bg-primary dark:hover:bg-primary dark:hover:text-white transition-all"
                  >
                    {isGenerating ? <Loader2 className="animate-spin" size={24}/> : <Sparkles size={24}/>} 
                    {isGenerating ? 'Chef IA pensando...' : 'Generar Receta'}
                  </button>
                  
                  <button 
                    onClick={() => setRecipeCreationMode('selection')}
                    className="w-full mt-4 py-4 text-stone-400 font-black uppercase tracking-widest text-[10px] hover:text-stone-600 transition-colors"
                  >
                    ← Volver a opciones
                  </button>
               </div>
             ) : <RecipeForm initialData={editingRecipe} onSave={saveRecipe} onCancel={() => setRecipeCreationMode('selection')} userId={session?.user?.id} />}
          </div>
        ) : null}
      </main>

      {/* Premium Modal */}
      <PremiumModal 
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        userId={session?.user?.id}
        onSuccess={() => {
          setShowPremiumModal(false);
          fetchAiUsage(session?.user?.id);
        }}
      />

      {recipeToDelete && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-stone-900/60 backdrop-blur-md">
           <div className="bg-white dark:bg-stone-900 w-full max-w-md rounded-[48px] p-10 shadow-2xl animate-in zoom-in-95">
              <h3 className="text-2xl font-black heading-serif text-center mb-10 dark:text-white">¿Eliminar receta?</h3>
              <div className="flex flex-col gap-4">
                 <button data-testid="confirm-delete-btn" onClick={confirmDeleteRecipe} disabled={isDeleting} className="w-full py-5 bg-rose-500 text-white rounded-[24px] font-black uppercase text-[10px]">{isDeleting ? 'Borrando...' : 'Confirmar'}</button>
                 <button onClick={() => setRecipeToDelete(null)} disabled={isDeleting} className="w-full py-5 bg-stone-100 text-stone-400 rounded-[24px] font-black uppercase text-[10px]">Cancelar</button>
              </div>
           </div>
        </div>
      )}

      <div className="fixed top-24 right-6 z-[60] flex flex-col gap-3 max-w-sm pointer-events-none">
        {toasts.map(t => (
          <div key={t.id} className="bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 p-5 rounded-[28px] shadow-2xl flex items-center gap-4 animate-in slide-in-from-right-10 pointer-events-auto">
             <div className="w-10 h-10 bg-primary/20 rounded-2xl flex items-center justify-center text-primary"><Heart size={20} fill="currentColor"/></div>
             <p className="text-sm font-bold">{t.notification.message}</p>
          </div>
        ))}
      </div>

      {!isCooking && !selectedRecipe && !['login', 'create', 'edit-profile'].includes(activeView) && (
        <button data-testid="fab-create-btn" onClick={() => requireAuth(() => { setEditingRecipe(null); setActiveView('create'); })} className="fixed bottom-10 right-10 w-20 h-20 md:w-24 md:h-24 bg-primary text-white rounded-full shadow-2xl z-40 flex items-center justify-center hover:scale-110 transition-all active:scale-95"><Plus size={32} className="md:hidden" /><Plus size={48} className="hidden md:block" /></button>
      )}

      {isCooking && selectedRecipe && <CookingMode recipe={selectedRecipe} onClose={() => setIsCooking(false)} onComplete={handleCookingComplete} settings={userSettings} />}
    </div>
  );
};

export default App;
